import React, {useContext, useState} from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {AppContext} from '../../contexts/AppContextProvider'
import {useForm} from '../../hooks/useForm'
import {PatientInfoState, ProfileOptions} from '../../contexts/types'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import OrgMappedImage from "../OrgMappedImage";
import PatientWeightInput from "../Inputs/PatientWeightInput";
import {setPatientInfo} from "../../contexts/PatientInfoReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";
import {setTreatmentDates} from "../../contexts/TreatmentDatesReducer";

const toothOptions = ['18', '17', '16', '15', '14', '13', '12', '11']
    .concat(['21', '22', '23', '24', '25', '26', '27', '28'])
    .concat(['38', '37', '36', '35', '34', '33', '32', '31'])
    .concat(['41', '42', '43', '44', '45', '46', '47', '48'])

const isSomethingChecked = (value: object) => {
    for (const [_, val] of Object.entries(value)) {
        if (val as boolean) return true
    }
    return false
}

const multiLangRegex = new RegExp(
    // This pattern:
    //
    // 1. Uses Unicode property escapes with the "u" flag.
    // 2. Specifically allows letters from the following scripts:
    //    - Latin       (English, French, Spanish, Portuguese, etc.)
    //    - Cyrillic    (Russian)
    //    - Han         (Chinese + Japanese Kanji)
    //    - Hiragana    (Japanese)
    //    - Katakana    (Japanese)
    // 3. Also allows digits (\p{N}), punctuation (\p{P}), symbols (\p{S}), 
    //    combining marks (\p{M}) for accents/diacritics, 
    //    and whitespace (\s).
    '^[\\p{Script=Latin}\\p{Script=Cyrillic}\\p{Script=Han}\\p{Script=Hiragana}\\p{Script=Katakana}\\p{N}\\p{P}\\p{S}\\p{M}\\s]*$',
    'u'
  )

const PatientInfo: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const {t, questionsConfiguration, orgIdentifier} = useContext(OrgCustomizationContext)
    const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>('lbs')

    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChangeEvent,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<PatientInfoState>({
        initialValues: {
            ...complaint.patientInfo,
        },
        validations: {
            patientId: {required},
            patientAge: {required},
            patientWeight: {
                required: {
                    value: questionsConfiguration.patient_weight?.required || false,
                    message: t('this_field_is_required')
                }
            },
            patientSex: {required},
            patientProfile: {
                required: {
                    value: (value, data) => {
                        return isSomethingChecked(value as object)
                    },
                    message: t('this_field_is_required'),
                },
            },
            patientProfileExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.patientProfile.OTHER &&
                            !data.patientProfileExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            hygiene: {
                required: {
                    value: complaint.generalComplaint.productType === 'IMPLANT',
                    message: t('this_field_is_required'),
                },
            },
            boneQuality: {
                required: {
                    value: complaint.generalComplaint.productType === 'IMPLANT',
                    message: t('this_field_is_required'),
                },
            },
            boneQualityExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            complaint.generalComplaint.productType ===
                            'IMPLANT' &&
                            data.boneQuality === 'OTHER' &&
                            !data.boneQualityExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            utn: {required},
            didDeviceContributeToInjury: {required},
            currentPatientStatus: {
                required: {
                    value: (value, data) => {
                        if (data.didDeviceContributeToInjury !== 'YES') {
                            return true
                        }
                        return !!value
                    },
                    message: t('this_field_is_required'),
                },
            },
            typeOfInjury: {
                required: {
                    value: (value, data) => {
                        if (data.didDeviceContributeToInjury !== 'YES') {
                            return true
                        }

                        return isSomethingChecked(value as object)
                    },
                    message: t('this_field_is_required'),
                },
            },
            typeOfInjuryExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.didDeviceContributeToInjury === 'YES' &&
                            !data.typeOfInjuryExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
        },
        changeWatchers: {
            utn: {
                setValue: (prevState, currentState, newState) => {
                    if (prevState.utn !== newState.utn) {
                        return {
                            ...newState,
                            fdi: toothOptions[parseInt(newState.utn) - 1],
                        }
                    }

                    if (prevState.fdi !== newState.fdi) {
                        return {
                            ...newState,
                            utn: (
                                toothOptions.indexOf(newState.fdi) + 1
                            ).toString(),
                        }
                    }
                    return newState
                },
            },
        },
    })

    const navigateToNextScreen = () => {
        navigateForward()
    }

    const handleIdChange = (value: string) => {
        if (multiLangRegex.test(value)) {
            handleChange('patientId', value)
        }
    }

    const handleAgeChange = (value: string) => {
        const re = /^(\d{0,3})$/
        const match = re.test(value)
        if (match) {
            handleChange('patientAge', value)
        }
    }

    const handleWeightChange = (value: string) => {
        handleChange('patientWeight', value)
    }

    const handleUtnSelection = (value: string) => {
        handleChange('utn', value)
    }

    const handleFdiSelection = (value: string) => {
        handleChange('fdi', value)
    }

    const handleProfileChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        let newProfile: ProfileOptions = {
            ...data.patientProfile,
            [event.target.name]: event.target.checked,
        }

        // If "None of the Above" is selected, then deselect all other options
        if (newProfile.NONE) {
            /* eslint-disable-next-line  @typescript-eslint/no-unused-vars */
            for (const [key, value] of Object.entries(newProfile)) {
                if ((key as keyof ProfileOptions) !== 'NONE') {
                    newProfile[key as keyof ProfileOptions] = false as boolean
                }
            }
        }

        handleChange('patientProfile', newProfile)
    }

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // If Other is selected for patient profile or bone quality,
        // then store the respective Explanation field values. Otherwise,
        // set the Explanation properties to empty strings
        // Also, if the selected Product Type is not Implant,
        // then store default values for Hygiene and Bone Quality fields.
        const patientInfo: PatientInfoState = {
            ...data,
            patientProfileExplanation: data.patientProfile.OTHER
                ? data.patientProfileExplanation
                : '',
            hygiene:
                complaint.generalComplaint.productType === 'IMPLANT'
                    ? data.hygiene
                    : '',
            boneQuality:
                complaint.generalComplaint.productType === 'IMPLANT'
                    ? data.boneQuality
                    : '',
            boneQualityExplanation:
                complaint.generalComplaint.productType === 'IMPLANT' &&
                data.boneQuality === 'OTHER'
                    ? data.boneQualityExplanation
                    : '',
            typeOfInjury:
                data.didDeviceContributeToInjury === 'YES'
                    ? data.typeOfInjury
                    : '',
            typeOfInjuryExplanation:
                data.didDeviceContributeToInjury === 'YES'
                    ? data.typeOfInjuryExplanation
                    : '',
            patientWeightUnit:
            weightUnit
        }

        dispatch(setPatientInfo(patientInfo))
        navigateToNextScreen()
    }

    return (
        <Grid container width='100%'>
            <Stack spacing={2} width='100%' sx={{border: 0}}>
                <Typography variant='h5' sx={{mb: 1}}>
                    {t('pi_patient_information')}
                </Typography>

                {/* Patient ID */}
                <FormControl sx={{mb: 2, maxWidth: '20rem'}}>
                    <FormLabel sx={{mb: 1}}>
                        {t('pi_patient_id_number')}:
                    </FormLabel>
                    <TextField
                        error={!!errors.patientId}
                        size='small'
                        value={data.patientId}
                        onChange={(e) => handleIdChange(e.target.value)}
                    />
                    <FormHelperText>
                        {t('pi_patient_id_helper_text')}
                    </FormHelperText>
                    <FormHelperText error={!!errors.patientId}>
                        {errors.patientId}
                    </FormHelperText>
                </FormControl>

                {/* Patient Age */}
                <FormControl sx={{mb: 2}}>
                    <FormLabel sx={{mb: 1}}>{t('pi_patient_age')}:</FormLabel>
                    <TextField
                        sx={{width: '9rem'}}
                        error={!!errors.patientAge}
                        size='small'
                        value={data.patientAge || ''}
                        onChange={(e) => handleAgeChange(e.target.value)}
                    />
                    <FormHelperText>
                        {t('pi_patient_age_helper_text')}
                    </FormHelperText>
                    <FormHelperText error={!!errors.patientAge}>
                        {errors.patientAge}
                    </FormHelperText>
                </FormControl>

                {/* Patient Weight */}
                <PatientWeightInput
                    errors={errors.patientWeight}
                    units={weightUnit}
                    setUnits={setWeightUnit}
                    weight={data.patientWeight}
                    onChange={handleWeightChange}
                />

                {/* Patient Sex */}
                <FormControl>
                    <FormLabel>{t('pi_patient_sex')}:</FormLabel>
                    <RadioGroup
                        row
                        value={data.patientSex}
                        onChange={handleChangeEvent('patientSex')}
                    >
                        <FormControlLabel
                            value='MALE'
                            control={<Radio/>}
                            label={t('male') as string}
                        />
                        <FormControlLabel
                            value='FEMALE'
                            control={<Radio/>}
                            label={t('female') as string}
                        />
                        <FormControlLabel
                            value='UNDISCLOSED'
                            control={<Radio/>}
                            label={t('undisclosed') as string}
                        />
                    </RadioGroup>
                    <FormHelperText error={!!errors.patientSex}>
                        {errors.patientSex}
                    </FormHelperText>
                </FormControl>

                {/* Patient Profile */}
                <Stack spacing={0}>
                    <FormControl>
                        <FormLabel>{t('pi_patient_profile')}:</FormLabel>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='BRUXER'
                                        checked={data.patientProfile.BRUXER}
                                        onChange={handleProfileChange}
                                        disabled={data.patientProfile.NONE}
                                    />
                                }
                                label={t('pi_bruxer') as string}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='DIABETIC'
                                        checked={data.patientProfile.DIABETIC}
                                        onChange={handleProfileChange}
                                        disabled={data.patientProfile.NONE}
                                    />
                                }
                                label={t('pi_diabetic') as string}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='SMOKER'
                                        checked={data.patientProfile.SMOKER}
                                        onChange={handleProfileChange}
                                        disabled={data.patientProfile.NONE}
                                    />
                                }
                                label={t('pi_smoker') as string}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='UNKNOWN'
                                        checked={data.patientProfile.UNKNOWN}
                                        onChange={handleProfileChange}
                                        disabled={data.patientProfile.NONE}
                                    />
                                }
                                label={t('pi_unknown') as string}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='OTHER'
                                        checked={data.patientProfile.OTHER}
                                        onChange={handleProfileChange}
                                        disabled={data.patientProfile.NONE}
                                    />
                                }
                                label={t('pi_other') as string}
                            />

                            {data.patientProfile.OTHER && (
                                <FormControl>
                                    <FormLabel sx={{mb: 1}}>
                                        {t('pi_please_specify')}:
                                    </FormLabel>
                                    <TextField
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        sx={{maxWidth: {sm: '35rem'}}}
                                        error={
                                            !!errors.patientProfileExplanation
                                        }
                                        helperText={
                                            errors.patientProfileExplanation
                                        }
                                        value={data.patientProfileExplanation}
                                        onChange={handleChangeEvent(
                                            'patientProfileExplanation'
                                        )}
                                    />
                                </FormControl>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name='NONE'
                                        checked={data.patientProfile.NONE}
                                        onChange={handleProfileChange}
                                    />
                                }
                                label={t('pi_none_of_the_above') as string}
                            />
                        </FormGroup>
                        <FormHelperText error={!!errors.patientProfile}>
                            {errors.patientProfile}
                        </FormHelperText>
                    </FormControl>
                </Stack>

                {/* Hygiene */}
                {/* Only show Hygiene fields if the Product Type is Implant */}
                {complaint.generalComplaint.productType === 'IMPLANT' && (
                    <FormControl
                        sx={{pt: 2}}
                        // error={!!errors.wasProductPlaced}
                    >
                        <FormLabel>{t('pi_hygiene_around_implant')}:</FormLabel>
                        <RadioGroup
                            value={data.hygiene}
                            onChange={handleChangeEvent('hygiene')}
                        >
                            <FormControlLabel
                                value='EXCELLENT'
                                control={<Radio/>}
                                label={t('checkbox_option_excellent') as string}
                            />
                            <FormControlLabel
                                value='GOOD'
                                control={<Radio/>}
                                label={t('checkbox_option_good') as string}
                            />
                            <FormControlLabel
                                value='FAIR'
                                control={<Radio/>}
                                label={t('checkbox_option_fair') as string}
                            />
                            <FormControlLabel
                                value='POOR'
                                control={<Radio/>}
                                label={t('checkbox_option_poor') as string}
                            />
                        </RadioGroup>
                        <FormHelperText error={!!errors.hygiene}>
                            {errors.hygiene}
                        </FormHelperText>
                    </FormControl>
                )}

                {/* Bone Quality */}
                {/* Only show Bone Quality fields if the Product Type is Implant */}
                {complaint.generalComplaint.productType === 'IMPLANT' && (
                    <Stack spacing={0}>
                        <FormControl>
                            <FormLabel>{t('pi_bone_quality')}:</FormLabel>
                            <RadioGroup
                                value={data.boneQuality}
                                onChange={handleChangeEvent('boneQuality')}
                            >
                                <FormControlLabel
                                    value='TYPE_1'
                                    control={<Radio/>}
                                    label={t('pi_type_1') as string}
                                />
                                <FormControlLabel
                                    value='TYPE_2'
                                    control={<Radio/>}
                                    label={t('pi_type_2') as string}
                                />
                                <FormControlLabel
                                    value='TYPE_3'
                                    control={<Radio/>}
                                    label={t('pi_type_3') as string}
                                />
                                <FormControlLabel
                                    value='TYPE_4'
                                    control={<Radio/>}
                                    label={t('pi_type_4') as string}
                                />
                                <FormControlLabel
                                    value='OTHER'
                                    control={<Radio/>}
                                    label={t('pi_other') as string}
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.boneQuality}>
                                {errors.boneQuality}
                            </FormHelperText>
                        </FormControl>
                        {data.boneQuality === 'OTHER' && (
                            <FormControl>
                                <FormLabel sx={{mb: 1}}>
                                    {t('pi_please_specify')}:
                                </FormLabel>
                                <TextField
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    sx={{maxWidth: {sm: '35rem'}}}
                                    error={!!errors.boneQualityExplanation}
                                    helperText={errors.boneQualityExplanation}
                                    value={data.boneQualityExplanation}
                                    onChange={handleChangeEvent(
                                        'boneQualityExplanation'
                                    )}
                                />
                            </FormControl>
                        )}
                    </Stack>
                )}
            </Stack>

            {/* Select a location */}
            <Grid item xs={12}>
                <Typography
                    variant='body1'
                    sx={{
                        mt: 2,
                        color: 'rgba(0, 0, 0, 0.6)',
                    }}
                >
                    {t('pi_location_of_device')}:
                </Typography>
            </Grid>

            {/* Image */}
            <Grid item xs={12} sm={7} md={5}>
                <Box
                    sx={{
                        border: 0,
                        display: 'flex',
                        direction: 'row',
                        justifyContent: {xs: 'center', sm: 'center'},
                    }}
                >
                    <OrgMappedImage imageKey='MouthDiagram'/>
                </Box>
            </Grid>

            {/* UTN/FDI */}
            <Grid
                item
                xs={12}
                sm={5}
                md={7}
                sx={{my: {sm: 'auto'}, border: 0}}
            >
                <>
                    <Stack direction='row' spacing={{xs: 2, sm: 1}} sx={{pl: {sm: 2}}}>
                        <FormControl>
                            <TextField
                                select
                                size='small'
                                id='select-utn'
                                sx={{
                                    width: {
                                        xs: '8rem',
                                        sm: '6rem',
                                        md: '9rem',
                                    },
                                    pr: {sm: 1},
                                }}
                                label={t('pi_utn')}
                                value={data.utn}
                                onChange={(e) =>
                                    handleUtnSelection(
                                        e.target.value.toString()
                                    )
                                }
                            >
                                {toothOptions.map((value, index) => {
                                    return (
                                        <MenuItem key={index} value={index + 1}>
                                            {index + 1}
                                        </MenuItem>
                                    )
                                })}
                            </TextField>
                        </FormControl>

                        <FormControl size='small'>
                            <TextField
                                select
                                size='small'
                                sx={{
                                    width: {
                                        xs: '8rem',
                                        sm: '6rem',
                                        md: '9rem',
                                    },
                                }}
                                id='select-fdi'
                                value={data.fdi}
                                label={t('pi_fdi')}
                                onChange={(e) =>
                                    handleFdiSelection(e.target.value)
                                }
                            >
                                {[...toothOptions]
                                    .sort()
                                    .map((value, index) => {
                                        return (
                                            <MenuItem key={index} value={value}>
                                                {value}
                                            </MenuItem>
                                        )
                                    })}
                            </TextField>
                        </FormControl>
                    </Stack>
                    <FormHelperText sx={{pl: {xs: 2, sm: 4}}}>
                        {t('pi_please_select_tooth_position')}
                    </FormHelperText>
                    <FormHelperText
                        sx={{pl: {xs: 2, sm: 4}}}
                        error={!!errors.utn}
                    >
                        {errors.utn}
                    </FormHelperText>
                </>
            </Grid>

            <Stack spacing={2}>
                {/* Did Device Contribute to Injury */}
                <FormControl sx={{pt: 2}}>
                    <FormLabel>
                        {t('pi_did_device_contribute_to_patient_injury')}
                    </FormLabel>
                    <RadioGroup
                        row
                        value={data.didDeviceContributeToInjury}
                        onChange={handleChangeEvent(
                            'didDeviceContributeToInjury'
                        )}
                    >
                        <FormControlLabel
                            value='YES'
                            control={<Radio/>}
                            label={t('option_yes') as string}
                        />
                        <FormControlLabel
                            value='NO'
                            control={<Radio/>}
                            label={t('option_no') as string}
                        />
                    </RadioGroup>
                    <FormHelperText
                        error={!!errors.didDeviceContributeToInjury}
                    >
                        {errors.didDeviceContributeToInjury}
                    </FormHelperText>
                </FormControl>

                {/* Type of Injury */}
                {data.didDeviceContributeToInjury === 'YES' && (
                    <>
                        <Stack spacing={0}>
                            <FormControl>
                                <FormLabel>{t('pi_type_of_injury')}:</FormLabel>
                                <RadioGroup
                                    value={data.typeOfInjury}
                                    onChange={handleChangeEvent('typeOfInjury')}
                                >
                                    <FormControlLabel
                                        control={<Radio/>}
                                        value='PERMANENT_IMPAIRMENT'
                                        label={
                                            t('pi_permanent_impairment') as string
                                        }
                                    />
                                    <FormControlLabel
                                        control={<Radio/>}
                                        value='MEDICAL_SURGICAL_INTERVENTION'
                                        label={
                                            t(
                                                'pi_medical_surgical_intervention'
                                            ) as string
                                        }
                                    />
                                    {orgIdentifier !== 'Nobel' && (
                                        <FormControlLabel
                                            control={<Radio/>}
                                            value='OTHER'
                                            label={t('pi_other') as string}
                                        />
                                    )}
                                </RadioGroup>
                                <FormHelperText error={!!errors.typeOfInjury}>
                                    {errors.typeOfInjury}
                                </FormHelperText>
                            </FormControl>
                        </Stack>

                        {/* More Details */}
                        <Stack>
                            <FormControl sx={{pt: 0}}>
                                <FormLabel sx={{mb: 1}}>
                                    {t('pi_more_details')}:
                                </FormLabel>
                                <TextField
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    error={!!errors.typeOfInjuryExplanation}
                                    helperText={errors.typeOfInjuryExplanation}
                                    value={data.typeOfInjuryExplanation}
                                    onChange={handleChangeEvent(
                                        'typeOfInjuryExplanation'
                                    )}
                                />
                            </FormControl>
                        </Stack>

                        {/* Patient Status */}
                        <Stack>
                            <FormControl sx={{pt: 2}}>
                                <FormLabel>{t('pi_patient_status')}:</FormLabel>
                                <RadioGroup
                                    value={data.currentPatientStatus}
                                    onChange={handleChangeEvent('currentPatientStatus')}
                                >
                                    <FormControlLabel
                                        value='RECOVERED'
                                        control={<Radio/>}
                                        label={t('pi_recovered_from_injury') as string}
                                    />
                                    <FormControlLabel
                                        value='RECOVERED_WITH_IMPAIRMENT'
                                        control={<Radio/>}
                                        label={
                                            t('pi_recovered_with_impairment') as string
                                        }
                                    />
                                    <FormControlLabel
                                        value='UNDER_TREATMENT'
                                        control={<Radio/>}
                                        label={t('pi_under_treatment') as string}
                                    />
                                    <FormControlLabel
                                        value='UNKNOWN'
                                        control={<Radio/>}
                                        label={t('pi_unknown') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText error={!!errors.currentPatientStatus}>
                                    {errors.currentPatientStatus}
                                </FormHelperText>
                            </FormControl>    
                        </Stack>
                    </>
                )}
            </Stack>

            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => {
                    dispatch(setPatientInfo(data))

                    const defaulted = (complaint.generalComplaint.wasAnotherImplantPlaced === 'YES' && complaint.complaintType.location === '4_CLINIC_DURING_PROCEDURE')

                    dispatch(setTreatmentDates({
                        ...complaint.treatmentDates,
                        wasProductRemoved: defaulted ? '' : complaint.treatmentDates.wasProductRemoved,
                        dateProductWasRemoved: defaulted ? null : complaint.treatmentDates.dateProductWasRemoved,
                    }))
                }}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default PatientInfo
